import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, Calendar, MapPin, Users, Heart, Book, Music } from 'lucide-react';
import { events, committeeMembers, sermons } from '../mockData';

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const heroImages = [
    'https://images.unsplash.com/photo-1667068114508-0055f7fb25a3?w=1920&q=80',
    'https://images.unsplash.com/photo-1667068114532-97a1ce6d0a52?w=1920&q=80',
    'https://images.unsplash.com/photo-1515616227676-603a1c547b3b?w=1920&q=80'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const generalPresident = committeeMembers.find(member => member.position === "General President");
  const otherMembers = committeeMembers.filter(member => member.position !== "General President");
  const featuredEvents = events.filter(event => event.featured);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          {heroImages.map((img, index) => (
            <div
              key={index}
              className={`hero-bg-image ${index === currentImageIndex ? 'active' : ''}`}
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
          <div className="hero-overlay" />
        </div>
        
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Uniting Churches. <br />
              Igniting Faith. <br />
              Transforming Communities.
            </h1>
            <p className="hero-subtitle">
              United Christian Fellowship brings believers together across churches and nations to worship, 
              grow, and walk in unity through Christ.
            </p>
            <div className="hero-buttons">
              <Link to="/watch-live" className="btn btn-primary">
                <Play size={24} />
                Watch Live
              </Link>
              <Link to="/about" className="btn btn-white">
                Learn More
                <ArrowRight size={24} />
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-line" />
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Mission of Unity</h2>
            <div className="title-underline" />
          </div>
          <p className="mission-text">
            United Christian Fellowship exists to bring churches together in unity, breaking denominational 
            barriers and building one body in Christ. Through worship, teaching, and fellowship, we create 
            spaces where believers grow spiritually and communities are transformed.
          </p>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="what-we-do-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What We Do</h2>
            <div className="title-underline" />
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1602022578110-fbfad5644f7f?w=800" 
                  alt="Worship & Music Festivals"
                  className="feature-image"
                />
                <div className="feature-icon-overlay">
                  <Music size={40} />
                </div>
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Worship & Music Festivals</h3>
                <p className="feature-description">
                  Powerful worship gatherings bringing believers together in praise and celebration of God's goodness.
                </p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1573339886518-264aa6f194b2?w=800" 
                  alt="Conferences & Conventions"
                  className="feature-image"
                />
                <div className="feature-icon-overlay">
                  <Book size={40} />
                </div>
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Conferences & Conventions</h3>
                <p className="feature-description">
                  Life-changing teachings from pastors and leaders around the world, equipping believers for ministry.
                </p>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1565813086292-604790c8a97b?w=800" 
                  alt="Community & Fellowship"
                  className="feature-image"
                />
                <div className="feature-icon-overlay">
                  <Users size={40} />
                </div>
              </div>
              <div className="feature-content">
                <h3 className="feature-title">Community & Fellowship</h3>
                <p className="feature-description">
                  Building strong relationships between churches and believers through authentic community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      <section className="events-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Featured Events</h2>
            <div className="title-underline" />
          </div>
          
          <div className="events-grid">
            {featuredEvents.map(event => (
              <div key={event.id} className="event-card">
                <div className="event-image-wrapper">
                  <img src={event.image} alt={event.title} className="event-image" />
                  <div className="event-date-badge">
                    <Calendar size={16} />
                    <span>{event.date}</span>
                  </div>
                </div>
                <div className="event-content">
                  <h3 className="event-title">{event.title}</h3>
                  <div className="event-location">
                    <MapPin size={16} />
                    <span>{event.location}</span>
                  </div>
                  <p className="event-description">{event.description}</p>
                  <Link to="/events" className="event-link">
                    Learn More <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="section-cta">
            <Link to="/events" className="btn btn-primary">
              View All Events
            </Link>
          </div>
        </div>
      </section>

      {/* Watch Live Section */}
      <section className="watch-live-section">
        <div className="watch-live-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1759127481171-30a27de310ad?w=1920)' }}>
          <div className="watch-live-overlay" />
        </div>
        <div className="container">
          <div className="watch-live-content">
            <h2 className="watch-live-title">Join Us Live</h2>
            <p className="watch-live-text">
              Experience powerful worship and teaching wherever you are. Join our live services and 
              special events streamed online.
            </p>
            <Link to="/watch-live" className="btn btn-primary btn-large">
              <Play size={24} />
              Watch Live Now
            </Link>
          </div>
        </div>
      </section>

      {/* General President Section */}
      {generalPresident && (
        <section className="president-section">
          <div className="container">
            <div className="president-content">
              <div className="president-image-wrapper">
                <img 
                  src={generalPresident.image} 
                  alt={generalPresident.name}
                  className="president-image"
                />
                <div className="president-glow" />
              </div>
              <div className="president-text">
                <div className="section-header">
                  <h2 className="section-title">Meet Our General President</h2>
                  <div className="title-underline" />
                </div>
                <h3 className="president-name">{generalPresident.name}</h3>
                <p className="president-description">
                  Our General President provides visionary leadership and spiritual guidance, championing unity, 
                  faith, and collaboration across churches. With a heart for God and passion for His people, 
                  our leadership inspires believers to live out their faith boldly and unite in Christ's mission.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Leadership Committee Section */}
      <section className="leadership-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Leadership & Committee</h2>
            <div className="title-underline" />
          </div>
          
          <div className="leadership-grid">
            {otherMembers.slice(0, 8).map(member => (
              <div key={member.id} className="leader-card">
                <div className="leader-image-wrapper">
                  <img src={member.image} alt={member.name} className="leader-image" />
                  <div className="leader-overlay">
                    <p className="leader-position">{member.position}</p>
                  </div>
                </div>
                <h4 className="leader-name">{member.name}</h4>
              </div>
            ))}
          </div>

          <div className="section-cta">
            <Link to="/leadership" className="btn btn-secondary">
              View Full Committee
            </Link>
          </div>
        </div>
      </section>

      {/* Prayer & Support Section */}
      <section className="prayer-section">
        <div className="prayer-bg" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1729089049653-24312fdca908?w=1920)' }}>
          <div className="prayer-overlay" />
        </div>
        <div className="container">
          <div className="prayer-content">
            <Heart size={48} className="prayer-icon" />
            <h2 className="prayer-title">You're Not Alone</h2>
            <p className="prayer-text">
              We believe in the power of prayer. Whatever you're going through, we are here to stand 
              with you in faith.
            </p>
            <Link to="/prayer-request" className="btn btn-primary">
              Submit a Prayer Request
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
